import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { AssetUrls } from 'config/assets';
import { useGetMeQuery } from 'store/services/user/meApi';
import { ImportSessionStorage } from 'utils/importSessionStorage';
import 'screens/Public/Elite/elite.css';
import './affiliates-landing.css';

// Same hero art as /eternaldurdles so the two pages share an identical hero treatment.
const HERO_SCRYFALL_ID = '546dae51-070c-4f3f-b0b4-6b88ff314af1';

const AffiliatesLandingPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  // Enrolled affiliates get a dashboard CTA instead of the enrollment one
  // (Eric's Option A ruling on the dashboard ticket: dashboard lives at
  // /affiliates/dashboard; this landing keeps /affiliates and becomes a soft
  // third entry point). isAffiliate is true only for active-status affiliates.
  const { data: meData } = useGetMeQuery(undefined, { skip: !currentUser });
  const isAffiliate = meData?.isAffiliate === true;

  const ctaLabel = isAffiliate ? 'View your dashboard' : 'Get Your Referral Link';

  const handleCta = () => {
    if (isAffiliate) {
      navigate('/affiliates/dashboard');
      return;
    }
    if (currentUser) {
      navigate('/settings/partners');
      return;
    }
    ImportSessionStorage.setReturnTo('/settings/partners');
    navigate('/welcome');
  };

  const heroBgUrl = AssetUrls.cards.getCardImage(HERO_SCRYFALL_ID, 'art_crop');

  return (
    <>
      <SEOHelmet
        title="TradeMagic Partner Program - Earn $5 Per Elite Referral"
        description="Join the TradeMagic Partner Program. Earn $5 for every verified Elite subscriber you refer, with monthly payouts via PayPal."
        canonical="https://trademagic.gg/affiliates"
        keywords="TradeMagic partner program, MTG referral program, earn money referring, Magic card trading"
        type="website"
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Partners', url: 'https://trademagic.gg/affiliates' },
        ]}
      />

      <div className="elite-layout">
        {isMobile && isMobileMenuOpen && (
          <div
            className="elite-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="elite-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/affiliates', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div
            className="elite-main elite-main--showcase"
            style={{ '--elite-hero-bg': `url(${heroBgUrl})` } as React.CSSProperties}
          >
            <TopNavigationBar
              title="Partners"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="elite-container">
              <section className="affil-landing-hero">
                <h1 className="affil-landing-hero__headline">
                  Earn $5 for every Elite subscriber you refer.
                </h1>
                <p className="affil-landing-hero__subhead">
                  Share your referral link. When someone you refer subscribes to TradeMagic
                  Elite, you earn a commission. Payouts are sent monthly via PayPal.
                </p>
                <button
                  className="elite-subscribe-button elite-subscribe-button--shimmer"
                  onClick={handleCta}
                >
                  {ctaLabel}
                </button>
              </section>

              <div className="affil-landing-sections">
                <section className="affil-landing-card">
                  <h2 className="affil-landing-card__title">How it works</h2>
                  <p className="affil-landing-card__body">
                    Share your referral link with friends, followers, or your playgroup. When
                    someone signs up through your link and subscribes to Elite, the conversion
                    is tracked to your account and you earn a commission.
                  </p>
                </section>

                <section className="affil-landing-card">
                  <h2 className="affil-landing-card__title">What you earn</h2>
                  <p className="affil-landing-card__body">
                    $5 per verified Elite conversion. Commissions are earned on a referred
                    user&apos;s first Elite subscription. Payouts are processed monthly via
                    PayPal.
                  </p>
                </section>

                <section className="affil-landing-card">
                  <h2 className="affil-landing-card__title">Who can join</h2>
                  <p className="affil-landing-card__body">
                    Any TradeMagic account in good standing can join. Enroll from your Settings
                    page to get your referral link and start sharing.
                  </p>
                </section>
              </div>

              <div className="affil-landing-cta-section">
                <button
                  className="elite-subscribe-button elite-subscribe-button--shimmer"
                  onClick={handleCta}
                >
                  {ctaLabel}
                </button>
                <p className="affil-landing-terms-note">
                  <Link to="/legal/affiliate-terms" className="affil-landing-terms-link">
                    View Full Terms &amp; Conditions
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>
    </>
  );
};

export default AffiliatesLandingPage;
